import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowRight } from 'lucide-react';
import { REGIME_OPTIONS, SERVICE_OPTIONS, PROFESSION_OPTIONS, onlyDigits, displayMoney } from './calcUtils';

export const CalculatorForm = ({ form, setForm, municipalities, onNext }) => {
  const [error, setError] = useState('');

  const handleMoney = (field) => (e) => {
    setForm({ ...form, [field]: onlyDigits(e.target.value) });
  };

  const handleNext = () => {
    if (!form.revenue || Number(form.revenue) <= 0) {
      setError('Informe seu faturamento mensal para continuar.');
      return;
    }
    if (!form.service) {
      setError('Selecione seu serviço predominante.');
      return;
    }
    if (form.service === 'outra_nao_medica' && !form.profession) {
      setError('Selecione qual é a sua profissão.');
      return;
    }
    setError('');
    onNext();
  };

  return (
    <div className="space-y-5" data-testid="calculator-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="calc-revenue">Faturamento mensal (R$) *</Label>
          <Input
            id="calc-revenue"
            data-testid="calc-revenue-input"
            inputMode="numeric"
            placeholder="Ex: 60.000"
            value={displayMoney(form.revenue)}
            onChange={handleMoney('revenue')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="calc-payroll">Folha de pagamento mensal (R$)</Label>
          <Input
            id="calc-payroll"
            data-testid="calc-payroll-input"
            inputMode="numeric"
            placeholder="Salários + pró-labore"
            value={displayMoney(form.payroll)}
            onChange={handleMoney('payroll')}
          />
        </div>
      </div>

      <div className={`grid grid-cols-1 ${form.service === 'outra_nao_medica' ? 'sm:grid-cols-2' : ''} gap-4`}>
        <div className="space-y-2">
          <Label>Serviço predominante *</Label>
          <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v, profession: v === 'outra_nao_medica' ? form.profession : '' })}>
            <SelectTrigger data-testid="calc-service-select">
              <SelectValue placeholder="Qual seu serviço predominante?" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {form.service === 'outra_nao_medica' && (
          <div className="space-y-2">
            <Label>Qual é a sua profissão? *</Label>
            <Select value={form.profession} onValueChange={(v) => setForm({ ...form, profession: v })}>
              <SelectTrigger data-testid="calc-profession-select">
                <SelectValue placeholder="Selecione sua profissão" />
              </SelectTrigger>
              <SelectContent>
                {PROFESSION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>Regime de tributação atual *</Label>
        <Select value={form.regime} onValueChange={(v) => setForm({ ...form, regime: v })}>
          <SelectTrigger data-testid="calc-regime-select">
            <SelectValue placeholder="Selecione seu regime atual" />
          </SelectTrigger>
          <SelectContent>
            {REGIME_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className={`grid grid-cols-1 ${form.regime === 'lucro_presumido_sup' ? 'sm:grid-cols-2' : ''} gap-4`}>
        <div className="space-y-2">
          <Label>Município da empresa *</Label>
          <Select value={form.municipality} onValueChange={(v) => setForm({ ...form, municipality: v })}>
            <SelectTrigger data-testid="calc-municipality-select">
              <SelectValue placeholder="Selecione o município" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {municipalities.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.nome}{m.uf ? ` - ${m.uf}` : ''} (ISS {m.iss_pct}%)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {form.regime === 'lucro_presumido_sup' && (
          <div className="space-y-2">
            <Label htmlFor="calc-partners">Número de sócios</Label>
            <Input
              id="calc-partners"
              data-testid="calc-partners-input"
              type="number"
              min="1"
              value={form.partners}
              onChange={(e) => setForm({ ...form, partners: e.target.value })}
            />
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-600" data-testid="calc-form-error">{error}</p>}

      <Button onClick={handleNext} className="w-full btn-primary rounded-md py-6 text-base font-semibold" data-testid="calc-next-button">
        Calcular meus impostos
        <ArrowRight className="ml-2 h-5 w-5" />
      </Button>
      <p className="text-xs text-slate-500 text-center">
        Simulação gratuita com base nas tabelas tributárias vigentes. Leva menos de 1 minuto.
      </p>
    </div>
  );
};
